<template>
  <article>
    <h3 v-if="title" class="c-info-data-title font-semibold text-sm lg:text-lg leading-tight" :class="{ 'text-white': theme === 'red' }">
      <span v-if="eventDate" :class="[{ 'text-app-secondary-8-green--vista-blue lg:text-app-secondary-9-green--sherpa-blue': !theme }, 't-cdp-info-sticky-date-' + theme]">
        {{ $d(eventDate, 'short-date') }}
      </span>
      {{ title }}
    </h3>

    <ul
      class="c-info-data-info grid text-sm"
      :class="{
        'mb-4 lg:mb-6': (url && saleIsAvailable) || streamUrl,
        'lg:text-white': theme === 'red',
        'grid-cols-2 gap-4': saleIsAvailableInFuture,
        'grid-cols-3': !saleIsAvailableInFuture,
      }"
    >
      <li v-if="eventDate">
        <h3>{{ $t('event-time') }}</h3>
        <time :datetime="eventDate.toISOString()" class="font-semibold shrink-0">
          <template v-if="eventEndDate">{{ $d(eventDate, 'short-time') }}<span class="px-1">–</span>{{ $d(eventEndDate, 'short-time') }}</template>
          <template v-else>{{ $d(eventDate, 'short-time') }}</template>
        </time>
      </li>
      <li v-if="location">
        <h3>{{ $t('event-location') }}</h3>
        <p class="font-semibold">{{ location }}</p>
      </li>
      <li v-if="price">
        <h3>{{ $t('event-from-price-title') }}</h3>
        <p class="font-semibold">{{ $t('event-from-price', { price: $n(price, 'currency') }) }}</p>
      </li>
      <li v-if="saleIsAvailableInFuture">
        <h3>{{ $t('event-detail-available-from-datetime') }}</h3>
        <p class="font-semibold">{{ $d(saleAvailableFromDate, 'long-date-time') }}</p>
      </li>
    </ul>

    <div :class="{ 'flex flex-col md:flex-row lg:flex-col shrink-0 items-stretch lg:items-start': url && saleIsAvailable && streamUrl }">
      <div v-if="url && saleIsAvailable" class="flex flex-col items-start shrink-0" :class="{ 'mb-3 md:mb-0 md:mr-3 lg:mr-0 lg:mb-3': streamUrl }">
        <MoleculeButton
          :url="url"
          class="c-info-data-button w-full sm:w-auto lg:w-full"
          background-color="bg-app-primary-1-orange--bittersweet hover:bg-black"
          :class="{ 'xl:w-2/3': !streamUrl }"
          @click="orderClick"
        >
          {{ $t('event-detail-order-button-label') }}
        </MoleculeButton>
        <MoleculeButton :url="url" size="medium" class="c-info-data-button-mobile-sticky" background-color="bg-white" text-color="text-black" @click="orderClick">
          {{ $t('event-detail-order-button-label') }}
        </MoleculeButton>
      </div>

      <div v-if="streamUrl" class="flex flex-col items-start shrink-0">
        <MoleculeButton
          :url="streamUrl"
          class="c-info-data-button w-full sm:w-auto lg:w-full"
          background-color="bg-app-primary-1-red--fire-brick lg:bg-white hover:bg-app-primary-1-orange--bittersweet"
          text-color="text-white lg:text-black hover:text-white"
          :class="{ 'xl:w-2/3': !url && !saleIsAvailable }"
        >
          {{ $t('event-detail-stream-button-label') }}
        </MoleculeButton>
        <MoleculeButton :url="streamUrl" size="medium" class="c-info-data-button-mobile-sticky" background-color="bg-white" text-color="text-black">
          {{ $t('event-detail-stream-button-label') }}
        </MoleculeButton>
      </div>

      <p v-if="saleIsAvailableInFuture" class="c-info-available-from-sticky-desktop text-sm font-medium">
        {{ $t('event-detail-available-from-datetime') }} {{ $d(saleAvailableFromDate, 'long-date-time') }}
      </p>
    </div>

    <div
      v-if="saleIcon && saleTemplate"
      :class="{ 'mt-6': url && saleIsAvailable }"
      class="c-info-data-status inline-flex items-center px-2 py-1 bg-app-primary-grey--black-squeeze rounded-md shadow-lg border-l-2 border-solid border-app-primary-1-red--torch"
    >
      <AtomIcon :icon="saleIcon" classes="w-4 mr-2 shrink-0 text-app-primary-1-red--torch" />
      <span class="text-xs leading-none font-semibold text-app-primary-1-red--fire-brick">
        {{ $t(saleTemplate) }}
      </span>
    </div>
    <div
      v-else-if="concertStatusLabel && concertStatusIcon"
      class="mt-4 c-info-data-status inline-flex items-center px-2 py-1 bg-app-primary-grey--black-squeeze rounded-md shadow-lg border-l-2 border-solid border-app-primary-1-red--torch"
      @click="switchInfoTicketsTab('other_data')"
    >
      <AtomLink :url="'#tab-other-data'" class="inline-flex items-center" target="_self">
        <AtomIcon :icon="concertStatusIcon" classes="w-4 mr-2 shrink-0 text-app-primary-1-red--torch" />
        <span class="text-xs leading-none font-semibold text-app-primary-1-red--fire-brick">{{ concertStatusLabel }}</span>
      </AtomLink>
    </div>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  eventDate: { type: Date, required: false, default: undefined },
  eventEndDate: { type: Date, required: false, default: undefined },
  location: { type: String, required: false, default: undefined },
  price: { type: Number, required: false, default: undefined },
  streamUrl: { type: String, required: false, default: undefined },
  theme: { type: String, required: false, default: undefined },
  title: { type: String, required: false, default: undefined },
  url: { type: String, required: false, default: undefined },

  // Sale information from `setupTixSale`
  tixSale: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
  // concert status from "IConcert"
  concertStatus: { type: Object as PropType<Record<string, any>>, required: false, default: () => ({}) },
})

const emit = defineEmits(['orderClick', 'switchInfoTicketsTab'])

const { saleAvailableFromDate, saleIcon, saleIsAvailable, saleIsAvailableInFuture, saleTemplate } = props.tixSale
const { concertStatusLabel, concertStatusIcon } = props.concertStatus

function orderClick() {
  emit('orderClick')
}

function switchInfoTicketsTab(tabName: string) {
  emit('switchInfoTicketsTab', tabName)
}
</script>

<style lang="scss">
// scoped scss is not suitable for this component, it won't work in this case because it needs to inherit styles from parent elements
.c-info-data-button,
.c-info-data-status {
  .i-is-stuck-on-mobile & {
    @apply hidden;
  }
}

.c-info-data-info {
  .i-is-stuck-on-mobile &,
  .i-is-stuck-on-desktop & {
    @apply hidden;
  }
}

.c-info-data-button-mobile-sticky {
  @apply hidden;

  .i-is-stuck-on-mobile & {
    @apply flex self-end;
  }
}

.c-info-available-from-sticky-desktop {
  @apply hidden;

  .i-is-stuck-on-desktop & {
    @apply block;
  }
}

.c-info-data-title {
  @apply hidden;

  .i-is-stuck-on-mobile & {
    @apply block mr-3;
  }

  .i-is-stuck-on-desktop & {
    @apply block mb-4;
  }
}
</style>
